import api from './api';

// ============================================
// Types
// ============================================

export interface DnsRecord {
  id: string;
  tenant_id: string;
  hostname: string;
  fqdn: string;
  ip_address: string;
  source: 'auto_device' | 'auto_connector' | 'manual';
  source_entity_id?: string;
  source_entity_type?: string;
  service_type_id?: string;
  service_port?: number;
  description?: string;
  connector_id?: string;
  is_enabled: number;
  created_at?: string;
  serviceType?: DnsServiceType;
  deviceGroupIds?: string[];
}

export interface DnsServiceType {
  id: string;
  tenant_id: string;
  name: string;
  display_name: string;
  protocol?: string;
  default_port?: number;
  url_scheme?: string;
  icon?: string;
  is_system: number;
}

export interface DnsSlugInfo {
  slug: string | null;
  zoneSuffix: string;
  fqdnExample: string | null;
}

export interface CreateDnsRecordData {
  hostname: string;
  ipAddress: string;
  serviceTypeId?: string;
  servicePort?: number;
  description?: string;
  connectorId?: string;
  deviceGroupIds?: string[];
}

export interface CreateServiceTypeData {
  name: string;
  displayName: string;
  protocol?: string;
  defaultPort?: number;
  urlScheme?: string;
  icon?: string;
}

// ============================================
// Service
// ============================================

export const dnsService = {
  // Records
  listRecords: async (search?: string, tenantId?: string) => {
    const params: Record<string, string> = {};
    if (search) params.search = search;
    if (tenantId) params.tenantId = tenantId;
    const response = await api.get('/dns/records', { params }) as any;
    return response.data as DnsRecord[];
  },

  createRecord: async (data: CreateDnsRecordData) => {
    const response = await api.post('/dns/records', data) as any;
    return response.data;
  },

  getRecord: async (id: string) => {
    const response = await api.get(`/dns/records/${id}`) as any;
    return response.data as DnsRecord;
  },

  updateRecord: async (id: string, data: Partial<CreateDnsRecordData & { isEnabled: boolean }>) => {
    const response = await api.put(`/dns/records/${id}`, data) as any;
    return response.data;
  },

  deleteRecord: async (id: string) => {
    const response = await api.delete(`/dns/records/${id}`) as any;
    return response.data;
  },

  assignGroups: async (recordId: string, groupIds: string[]) => {
    const response = await api.post(`/dns/records/${recordId}/groups`, { groupIds }) as any;
    return response.data;
  },

  // Service Types
  listServiceTypes: async () => {
    const response = await api.get('/dns/service-types') as any;
    return response.data as DnsServiceType[];
  },

  createServiceType: async (data: CreateServiceTypeData) => {
    const response = await api.post('/dns/service-types', data) as any;
    return response.data;
  },

  deleteServiceType: async (id: string) => {
    const response = await api.delete(`/dns/service-types/${id}`) as any;
    return response.data;
  },

  // Slug
  getSlug: async () => {
    const response = await api.get('/dns/slug') as any;
    return response.data as DnsSlugInfo;
  },

  updateSlug: async (slug: string, tenantId?: string) => {
    const params = tenantId ? { tenantId } : {};
    const response = await api.put('/dns/slug', { slug }, { params }) as any;
    return response.data;
  },

  // Zone preview
  getZonePreview: async () => {
    const response = await api.get('/dns/zone-preview') as any;
    return response.data;
  },
};
